<template>
    <ag-grid-vue
        v-if="rows.length"
        :grid-options="gridOptions"
        :row-data="rows"
        :column-defs="colDefs"
        class="sub-account-ledger-grid"
        :theme="agTheme"
        :get-row-class="getDynamicRowClass"
        :row-class-rules="rowClassRules"
        @selection-changed="onSelectionChanged"
        @row-drag-enter="onRowDragEnter"
        @row-drag-move="onRowDragMove"
        @row-drag-leave="onRowDragLeave"
        @row-drag-end="onRowDragEnd"
    />
    <div v-else>
        Ingen poster i perioden
    </div>
</template>

<script lang="ts" setup>

import {inject, onMounted, Ref, ref, shallowRef, watch} from 'vue'
import {AgGridVue} from 'ag-grid-vue3'
import {useSubAccounts} from '@/subaccount/subAccount.service'
import {agTheme} from '@/agUtils/ag-theme.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    RowClassParams,
    RowDragEndEvent,
    RowDragEnterEvent,
    RowDragMoveEvent, ValueGetterParams, ValueSetterParams
} from 'ag-grid-community'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {SubAccountLedgerFilter} from '@/subaccount/outstandingBalance/SubAccountLedgerFilter'
import {LedgerSubAccount} from '@/subaccount/types/SubAccountLedger'
import TransactionLinkCell from '@/transaction/TransactionLinkCell.vue'
import SubAccountInvoiceLinkCell from '@/subaccount/outstandingBalance/SubAccountInvoiceLinkCell.vue'
import {SaccLineConnUuid} from '@/subaccount/outstandingBalance/SaccLineConn'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {SACC_LINES_UPDATED} from '@/types/transaction/TransactionEvent'
import SubAccountLineActions from '@/subaccount/outstandingBalance/SubAccountLineActions.vue'
import {canAddPair} from '@/subaccount/outstandingBalance/SaccLineConnBuilder'
import PendingRemainingAmountCell from '@/subaccount/components/PendingRemainingAmountCell.vue'
import SubAccountLedgerDescriptionCell from '@/subaccount/outstandingBalance/SubAccountLedgerDescriptionCell.vue'
import {ORG_SELECTED} from '@/types/events'
import SubAccountLedgerAttachmentCell from '@/subaccount/outstandingBalance/SubAccountLedgerAttachmentCell.vue'

const {fetchSubAccountLines, closePosts, disconnectLine} = useSubAccounts()
const {onEvent} = useEmitter()

const props = defineProps({
    ledgerEntry: {
        type: Object as () => LedgerSubAccount,
        required: true
    },
    subAccountFilter: {
        type: Object as () => SubAccountLedgerFilter,
        required: true
    },
    isNotConnected: {
        type: Function,
        required: true
    },
    autoConnect: {
        type: Boolean,
        required: true
    },
    isCustomer: {
        type: Boolean,
        required: true
    }
})

const hightlightMoveOverIndex = ref<number>()
const draggingItem = ref<SubAccountLine>()
const gridApi = shallowRef<GridApi>()


const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')

const emit = defineEmits(['toggleChosenLine', 'setChosenLines', 'magicWand', 'createPaymentTransaction', 'createAutoPaymentTransaction'])

const currencyCellRenderer = (param: ICellRendererParams) => {
    return formatCurrency(param.value, 2)
}

const rows = ref<SubAccountLine[]>([])

const setSelectedNodes = () => {
    if (gridApi.value) {
        const nodesToSelect: IRowNode[] = []
        gridApi.value.forEachNode((node: IRowNode) => {
            if (!node.data.isOpen) nodesToSelect.push(node)
        })
        gridApi.value.setNodesSelected({nodes: nodesToSelect, newValue: true})
    }
}

const gridOptions: GridOptions<SubAccountLine> = {
    onGridReady: ({api}: GridReadyEvent) => {
        gridApi.value = api
        setSelectedNodes()
    },
    getRowId: ({data}: { data: SubAccountLine }) => data?.uuid,
    enableCellTextSelection: true,
    suppressCellFocus: true,
    suppressDragLeaveHidesColumns: true,
    rowSelection: {
        isRowSelectable: (node: IRowNode) => node.data.pendingTransactions.length === 0,
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false
    },
    context: {
        panelId,
        panelPathSegment,
    },
    popupParent: document.body,
}

const getDynamicRowClass = (params: RowClassParams) => {
    return params.rowIndex === hightlightMoveOverIndex.value ? '!bg-primary/50 !border !border-primary' : ''
}

const matchedPair = ref<{ line1: SubAccountLine, line2: SubAccountLine }[]>([])

const findRowWithMatchingAmount = (line: SubAccountLine) => {
    if (line.amount === 0) return
    const matchingRow = rows.value.find(r => r.remainingAmount === -line.amount && r.invoiceUuid === line.invoiceUuid) || rows.value.find(r => r.remainingAmount === -line.amount)
    if (matchingRow && !matchedPair.value.some(pair => pair.line1 === matchingRow || pair.line2 === matchingRow)) {
        matchedPair.value.push({line1: matchingRow, line2: line})
    }
    return matchingRow
}

const hasMatchingPosts = (params: RowClassParams) => {
    return !!findRowWithMatchingAmount(params.data)
}

const checkIfValidDragTarget = (params: RowClassParams) => {
    if (!draggingItem.value) return false
    return draggingItem.value.amount * params.data.amount > 0
}

const checkIfOptimalDragTarget = (params: RowClassParams) => {
    if (!draggingItem.value) return false
    return draggingItem.value.amount === params.data.amount * -1
}

const showMagicWand = () => {
    if (rows.value.length < 2) {
        emit('magicWand', false)
        return
    }
    emit('magicWand', rows.value.some(r => findRowWithMatchingAmount(r)))
}

const rowClassRules = {
    'matching-posts': hasMatchingPosts,
    'indicate-invalid-drag-target': checkIfValidDragTarget,
    'optimal-drag-target': checkIfOptimalDragTarget
}

const connectLinesAndRefresh = async (line1: SubAccountLine, line2: SubAccountLine) => {
    let debitLineUuid, creditLineUuid
    if (line1.amount > line2.amount) {
        debitLineUuid = line1.uuid
        creditLineUuid = line2.uuid
    } else {
        debitLineUuid = line2.uuid
        creditLineUuid = line1.uuid
    }

    await closePosts([{
        debitLineUuid: debitLineUuid,
        creditLineUuid: creditLineUuid
    }])
    emitter.emit(SACC_LINES_UPDATED)
    await fetchData()
}

const disconnectLinesAndRefresh = async (line: SubAccountLine) => {
    await disconnectLine(line.uuid)
    emitter.emit(SACC_LINES_UPDATED)
    await fetchData()
}

const handleSaccLinesClicked = async (data: any) => {
    if (!data.isOpen || data.remainingAmount !== data.amount) {
        await disconnectLinesAndRefresh(data)
    } else if (rows.value.some(row => row.remainingAmount === -data.remainingAmount)) {
        const matchingRow = rows.value.find(row => row.remainingAmount === -data.remainingAmount)!
        await connectLinesAndRefresh(matchingRow, data)
    }
}

const handleCreatePaymentTransaction = (subAccountLine: SubAccountLine) => emit('createPaymentTransaction', subAccountLine)

const colDefs: ColDef[] = [
    {
        headerName: 'Dato',
        field: 'date',
        width: 100,
        cellRenderer: (node: ICellRendererParams) => formattedDate(node.value)
    },
    {
        headerName: 'Bilag',
        field: 'uuid',
        width: 90,
        cellRenderer: TransactionLinkCell,
        cellStyle: {
            overflow: 'visible',
        },
        contextMenuItems: []
    },
    {
        headerName: 'Faktura',
        field: 'invoiceNo',
        width: 120,
        cellRenderer: SubAccountInvoiceLinkCell,
        contextMenuItems: []
    },
    {
        headerName: 'Beskrivelse',
        field: 'description',
        flex: 2,
        minWidth: 80,
        width: 400,
        cellRenderer: SubAccountLedgerDescriptionCell
    },
    {
        headerName: 'Vedlegg',
        field: 'attachment',
        width: 300,
        minWidth: 70,
        flex: 1,
        cellRenderer: SubAccountLedgerAttachmentCell,
        contextMenuItems: []
    },
    {
        headerName: 'Forfall',
        field: 'dueDate',
        width: 100,
        cellRenderer: (node: ICellRendererParams) => formattedDate(node.value)
    },
    {
        headerName: 'Beløp',
        headerClass: 'header-right',
        field: 'amount',
        cellClass: 'amount-cell text-right',
        cellRenderer: currencyCellRenderer,
        width: 110,
    },
    {
        headerClass: 'header-right',
        headerName: 'Gjenstående',
        cellClass: 'amount-cell text-right',
        field: 'remainingAmount',
        width: 110,
        cellRenderer: currencyCellRenderer
    },
    {
        headerName: 'Til betaling',
        headerClass: 'header-right',
        cellClass: 'remaining-amount-cell text-right',
        cellRenderer: PendingRemainingAmountCell,
        width: 100,
    },
    {
        headerName: '',
        field: 'checkboxCol',
        minWidth: 55,
        maxWidth: 55,
        editable: true,
        cellRenderer: 'agCheckboxCellRenderer',
        valueGetter: (params: ValueGetterParams) => {
            return params.node?.isSelected()
        },
        valueSetter: (params: ValueSetterParams) => {
            params.node?.setSelected(params.newValue)
            return true
        },
    },
    {
        headerClass: 'header-right',
        cellClass: '!flex items-center justify-start',
        flex: 0.2,
        minWidth: 50,
        cellRenderer: SubAccountLineActions,
        cellRendererParams: {
            isNotConnected: props.isNotConnected,
            isCustomer: props.isCustomer,
            rows,
            handleSaccLinesClicked,
            handleCreatePaymentTransaction,
        },
    },
]

const onSelectionChanged = () => {
    const selectedRows: SubAccountLine[] = gridApi.value?.getSelectedRows() || []
    emit('setChosenLines', selectedRows)
    gridApi.value?.redrawRows()
}

const clearSelection = () => {
    if (gridApi.value) {
        gridApi.value.deselectAll()
    }
}

const clearRows = () => {
    rows.value = []
    matchedPair.value = []
    hightlightMoveOverIndex.value = -1
}

watch(gridApi, (newValue) => {
    if (newValue) {
        clearSelection()
    }
})

const onRowDragEnter = (event: RowDragEnterEvent) => {
    draggingItem.value = event.node.data
}
const onRowDragMove = (event: RowDragMoveEvent) => {
    hightlightMoveOverIndex.value = event.overIndex
}
const onRowDragLeave = () => {
    draggingItem.value = undefined
    hightlightMoveOverIndex.value = -1
}
const onRowDragEnd = async (event: RowDragEndEvent) => {
    const draggedNode = event.node
    const targetNode = event.overNode
    draggingItem.value = undefined
    hightlightMoveOverIndex.value = -1
    if (!draggedNode || !targetNode) {
        return
    }
    if (draggedNode.data.uuid === targetNode.data.uuid) {
        return
    }

    //If dragging onto same amount sign, return
    if (draggedNode.data.amount * targetNode.data.amount > 0) {
        return
    }
    await connectLinesAndRefresh(draggedNode.data, targetNode.data)
}

watch(hightlightMoveOverIndex, () => {
    gridApi.value?.redrawRows()
})

const fetchData = async () => {
    const filter = {
        start: props.subAccountFilter.start,
        end: props.subAccountFilter.end,
        isOpen: props.subAccountFilter.isOpen,
        size: 100,
        query: null
    }
    const page = await fetchSubAccountLines(props.ledgerEntry?.subAccountUuid, filter)
    rows.value = page.content
    showMagicWand()
    gridApi.value?.deselectAll()
}

const handleAutoConnect = async () => {
    const connections: SaccLineConnUuid[] = []
    matchedPair.value.forEach(pair => {
        const debitLineUuid = pair.line1.amount > 0 ? pair.line1.uuid : pair.line2.uuid
        const creditLineUuid = pair.line1.amount > 0 ? pair.line2.uuid : pair.line1.uuid
        if (canAddPair(connections, debitLineUuid, creditLineUuid)) {
            connections.push({debitLineUuid, creditLineUuid})
        }
    })
    await closePosts(connections)
    emitter.emit(SACC_LINES_UPDATED)
    await fetchData()
}

onMounted(() => {
    fetchData()
    onEvent(ORG_SELECTED, () => {
        clearRows()
    })
})
defineExpose({fetchData})

watch(() => [props.ledgerEntry], () => {
    fetchData()
})

watch(() => [props.autoConnect], async () => {
    if (props.autoConnect) {
        await handleAutoConnect()
        emit('magicWand', false)
    }
})

</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";
.sub-account-ledger-grid {
    .ag-body-horizontal-scroll {
        display: none;
    }

    .matching-posts {
        [col-id='remainingAmount'] {
            @apply bg-primary/25;
        }
    }

    .indicate-invalid-drag-target {
        @apply opacity-20;
    }

    .optimal-drag-target {
        @apply bg-primary/20;
    }

    .ag-row-hover .sub-account-line-actions {
        @apply block;
    }

}
</style>